// The video recorder, with a viewfinder in it. Lifted from the Andon app's
// `VideoRecorder`.
//
// WHAT THIS REPLACES.
//
// A line of text that said "Recording video" and nothing else. So an operator
// was pointing a phone at a machine and guessing — and every clip sent that way
// was a gamble, with the ones that missed strictly worse than no clip at all,
// because the responder had already spent their attention watching them. A
// preview is not a nicety here: it is the only way to know the rack label is in
// frame.
//
// FULL SCREEN, AND NOT A SHEET.
//
// A viewfinder in a bottom sheet is a viewfinder the size of a postage stamp
// with the thing being filmed cropped out of it. This takes the screen, the
// way every camera does, and the controls sit over the frame rather than beside
// it. You watch it back before it sends.

import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/ui/Icon';
import type { Recorder } from '@/hooks/useRecorder';
import { useT } from '@/i18n/useT';
import { clipLength } from '@/lib/capture';

export function VideoRecorder({
  recorder,
  onSend,
  onDiscard,
}: {
  recorder: Recorder;
  /** The reviewed clip, accepted. */
  onSend: (clip: { blob: Blob; name: string; seconds: number }) => void;
  /** Backed out of, at either stage. Nothing has been staged. */
  onDiscard: () => void;
}) {
  const t = useT();
  const videoRef = useRef<HTMLVideoElement>(null);
  /** The recorded clip, held for review. Null while still filming. */
  const [clip, setClip] = useState<{ blob: Blob; name: string; seconds: number } | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // ── the live frame ──────────────────────────────────────────────────────
  //
  // `srcObject` rather than a src URL, and set imperatively because React does
  // not treat it as a prop. Re-run on the stream IDENTITY, which is what the
  // lens flip changes deliberately — mutating the tracks of the same object
  // does not always make the element repaint.
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !recorder.stream) return;
    el.srcObject = recorder.stream;
    // Autoplay on a muted inline element is permitted everywhere; the promise
    // rejects if the element is torn down first, which is not a failure worth
    // reporting to somebody holding a camera.
    void el.play().catch(() => {});
    return () => {
      el.srcObject = null;
    };
  }, [recorder.stream]);

  // ── the recorded clip, for review ───────────────────────────────────────
  //
  // A blob URL, revoked when it goes. Left un-revoked it holds the whole video
  // in memory for the life of the page, and on a phone that has recorded five
  // clips in a shift that is the thing that kills the WebView.
  useEffect(() => {
    if (!clip) return;
    const url = URL.createObjectURL(clip.blob);
    setPreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
      setPreviewUrl(null);
    };
  }, [clip]);

  const stop = async () => {
    setBusy(true);
    try {
      const out = await recorder.stop();
      // Null means nothing usable was captured — a tap so fast the recorder
      // never wrote a chunk. Backing straight out is right: there is nothing to
      // review and nothing to apologise for.
      if (!out) onDiscard();
      else setClip(out);
    } finally {
      setBusy(false);
    }
  };

  const reviewing = clip != null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={reviewing ? t.videoWatchItBackT : t.videoRecordingT}
      style={{ position: 'fixed', inset: 0, zIndex: 40, background: '#050609', display: 'flex', flexDirection: 'column' }}
    >
      {/* ── the frame ─────────────────────────────────────────────────────── */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
        {reviewing && previewUrl ? (
          <video src={previewUrl} controls playsInline autoPlay style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#050609' }} />
        ) : (
          <video
            ref={videoRef}
            muted
            playsInline
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              // The front lens is mirrored, the way every camera app mirrors
              // it: unmirrored self-view is disorienting enough that people
              // stop filming.
              transform: recorder.facing === 'user' ? 'scaleX(-1)' : undefined,
            }}
          />
        )}
      </div>

      {/* Scrims, so white controls stay readable over a bright frame. */}
      <div aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, top: 0, height: 120, background: 'linear-gradient(180deg,rgba(5,6,9,.8),transparent)' }} />
      <div aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 190, background: 'linear-gradient(0deg,rgba(5,6,9,.85),transparent)' }} />

      {/* ── top bar: the clock, the limit, and the way out ────────────────── */}
      <div style={{ position: 'relative', flex: 'none', padding: 'calc(18px + env(safe-area-inset-top)) 20px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
        {!reviewing && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 7, height: 30, padding: '0 11px', borderRadius: 15, background: 'rgba(255,90,95,.9)' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', animation: 'ipRec 1.1s infinite' }} />
            <span className="numeric" style={{ font: "700 12px 'Space Grotesk',sans-serif", color: '#fff' }}>
              {clipLength(recorder.seconds)}
            </span>
          </span>
        )}
        <span style={{ font: '600 11px Manrope,sans-serif', color: 'rgba(243,245,247,.8)' }}>
          {reviewing ? `${clipLength(clip.seconds)} — ${t.videoWatchItBackT}` : `${t.videoMaxT} ${recorder.maxSeconds}s · 1080p`}
        </span>
        <button
          type="button"
          aria-label={t.videoDiscardCloseT}
          onClick={() => {
            // Cancel is safe at either stage: filming, it stops the tracks and
            // throws the chunks away; reviewing, the recorder is already
            // released and this is a no-op.
            recorder.cancel();
            onDiscard();
          }}
          style={{
            marginLeft: 'auto',
            width: 36,
            height: 36,
            borderRadius: 12,
            background: 'rgba(20,22,28,.7)',
            border: '1px solid rgba(255,255,255,.14)',
            display: 'grid',
            placeItems: 'center',
            color: '#f3f5f7',
            cursor: 'pointer',
          }}
        >
          <Icon name="cross" size={17} strokeWidth={2} />
        </button>
      </div>

      <div style={{ flex: 1 }} />

      {/* ── bottom controls ──────────────────────────────────────────────── */}
      <div style={{ position: 'relative', flex: 'none', padding: '0 20px calc(26px + env(safe-area-inset-bottom))' }}>
        {reviewing ? (
          <div style={{ display: 'flex', gap: 9 }}>
            <button
              type="button"
              className="press-soft"
              onClick={() => {
                setClip(null);
                void recorder.start();
              }}
              style={{
                flex: 1,
                height: 52,
                borderRadius: 15,
                background: 'rgba(20,22,28,.85)',
                border: '1px solid rgba(255,255,255,.16)',
                color: '#f3f5f7',
                font: "700 14px 'Space Grotesk',sans-serif",
              }}
            >
              {t.videoFilmAgainT}
            </button>
            <button
              type="button"
              className="press-soft"
              onClick={() => onSend(clip)}
              style={{ flex: 1.3, height: 52, borderRadius: 15, background: 'var(--pos)', border: 0, color: '#fff', font: "700 14px 'Space Grotesk',sans-serif" }}
            >
              {t.videoUseClipT}
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              {/* The lamp, only where the hardware says it exists. A control
                  that does nothing reads as a broken app. */}
              <RoundControl
                label={recorder.torchOn ? t.videoTorchOffT : t.videoTorchOnT}
                icon="torch"
                on={recorder.torchOn}
                hidden={!recorder.torchAvailable}
                onClick={() => void recorder.toggleTorch()}
              />
              <button
                type="button"
                aria-label={t.videoStopT}
                disabled={busy}
                onClick={() => void stop()}
                style={{
                  width: 74,
                  height: 74,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,.14)',
                  border: '3px solid #fff',
                  display: 'grid',
                  placeItems: 'center',
                  cursor: 'pointer',
                  opacity: busy ? 0.6 : 1,
                }}
              >
                <span style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--danger)' }} />
              </button>
              <RoundControl label={t.videoFlipT} icon="cameraFlip" onClick={() => void recorder.flipCamera()} />
            </div>
            <p style={{ margin: '14px 0 0', textAlign: 'center', font: '500 11.5px/1.5 Manrope,sans-serif', color: 'rgba(243,245,247,.75)' }}>
              {t.videoTapToStopT}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function RoundControl({
  label,
  icon,
  onClick,
  on = false,
  hidden = false,
}: {
  label: string;
  icon: 'torch' | 'cameraFlip';
  onClick: () => void;
  on?: boolean;
  hidden?: boolean;
}) {
  // Kept in the layout when unavailable, so the stop button stays centred.
  // A shutter that jumps sideways depending on the hardware is a shutter people
  // miss.
  if (hidden) return <span aria-hidden="true" style={{ width: 46, height: 46 }} />;
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={on}
      onClick={onClick}
      style={{
        width: 46,
        height: 46,
        borderRadius: 14,
        background: on ? 'rgba(240,161,58,.85)' : 'rgba(20,22,28,.7)',
        border: `1px solid ${on ? 'var(--warn)' : 'rgba(255,255,255,.14)'}`,
        display: 'grid',
        placeItems: 'center',
        color: on ? '#14161c' : '#f3f5f7',
        cursor: 'pointer',
      }}
    >
      <Icon name={icon} size={20} strokeWidth={1.8} />
    </button>
  );
}
